//
// # ReportIssueDialog
//
// In-IDE bug-report modal (issue #2146). Description + optional
// category; the rest of the IssueReport context (session, user, tenant,
// design ident, commit hash, env) is auto-captured and shown to the
// user before submit. No design content is uploaded — only the depot
// identifier — and there's no screenshot capture yet (deferred).
//
// On submit the dialog posts via reportIssue.postReportIssue, surfaces
// a status message on the workspace status bar, and closes. Errors
// stay in-place with an inline message so the user can correct and
// retry without re-typing.
//

import React, { useCallback, useMemo, useState } from "react";

import { Ident } from "GenAlphaModel";
import type { IssueReport, IssueReportCategory } from "ServerInterface";

import { Modal } from "../Modal/Modal";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import {
  createRequestContext,
  getReportIssueDefaults,
} from "../../contextHelpers";
import { postReportIssue } from "../../reportIssue";

interface CategoryOption {
  readonly value: IssueReportCategory;
  readonly label: string;
}

const CATEGORIES: readonly CategoryOption[] = [
  { value: "crash", label: "Crash" },
  { value: "wrong-result", label: "Wrong result" },
  { value: "slow", label: "Slow" },
  { value: "ui", label: "UI glitch" },
  { value: "ai", label: "AI quality" },
  { value: "other", label: "Other" },
];

export interface ReportIssueDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function ReportIssueDialog({ open, onClose }: ReportIssueDialogProps) {
  const workspace = useWorkspace();
  const snapshot = useWorkspaceSnapshot();

  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<IssueReportCategory | undefined>(
    undefined
  );
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Read identity / session defaults once per open. The values come from
  // module state so they're stable across re-renders, but recomputing
  // them when `open` flips lets the preview reflect any late-arriving
  // sessionId / userEmail set after the modal first mounted.
  const defaults = useMemo(
    () => (open ? getReportIssueDefaults() : null),
    [open]
  );

  const activeIdent = snapshot.activeIdent;
  const designSummary = activeIdent ? Ident.displayName(activeIdent) : null;
  // Only hierpath idents have a `path` array; builtin/libcellview don't.
  // For libcellview we still report a synthetic path so triage has something
  // to grep; builtins don't get a designIdent.
  const designIdentForReport: IssueReport["designIdent"] = (() => {
    if (!activeIdent) return undefined;
    if (activeIdent.kind === "hierpath") {
      return { kind: "hierpath", path: activeIdent.path };
    }
    if (activeIdent.kind === "libcellview") {
      return {
        kind: "hierpath",
        path: [activeIdent.lib, activeIdent.cell, activeIdent.view],
      };
    }
    return undefined;
  })();

  const reset = useCallback(() => {
    setDescription("");
    setCategory(undefined);
    setSubmitting(false);
    setErrorMessage(null);
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    reset();
    onClose();
  }, [submitting, reset, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!description.trim() || submitting || !defaults) return;
    setSubmitting(true);
    setErrorMessage(null);

    const report: IssueReport = {
      // Fresh request context so the server-side log entries correlate
      // with the IDE-side ones (shared logId / sessionId / tenant).
      context: createRequestContext().toWire(),
      sessionId: defaults.sessionId,
      timestamp: new Date().toISOString(),
      description: description.trim(),
      category,
      user: {
        email: defaults.userEmail,
        tenant: defaults.tenant,
      },
      designIdent: designIdentForReport,
      env: defaults.env,
      commitHash: defaults.commitHash,
    };

    const result = await postReportIssue(
      {
        alphaServerUrl: workspace.client.args.alphaServerUrl,
        getToken: workspace.client.args.getToken,
      },
      report
    );

    if (result.err) {
      const why = result.val.why;
      const msg =
        why === "rate-limited"
          ? "You're filing reports too fast — please wait up to a minute and try again."
          : why === "auth-required"
          ? "Sign-in required to file an issue."
          : why === "invalid-body"
          ? "Report rejected — description is required."
          : why === "network-error"
          ? "Network error — check your connection and try again."
          : why === "server-error"
          ? `Server error (${result.val.status}) — please try again.`
          : "Unexpected error — please try again.";
      setErrorMessage(msg);
      setSubmitting(false);
      return;
    }

    // v1 has no GitHub Issues sink, so trackingUrl is usually undefined.
    // When a future sink populates it, surface the URL in the toast.
    const trackingUrl = result.val.trackingUrl;
    const statusText =
      trackingUrl?.startsWith("http") === true
        ? `Issue reported — tracked at ${trackingUrl}`
        : "Issue reported.";
    workspace.setStatusMessage({ text: statusText, kind: "info" }, 8000);
    reset();
    onClose();
  }, [
    description,
    category,
    submitting,
    defaults,
    designIdentForReport,
    workspace,
    reset,
    onClose,
  ]);

  return (
    <Modal open={open} onClose={handleClose} title="Report an Issue">
      <div className="flex flex-col gap-3">
        <label
          className="text-xs font-semibold"
          style={{ color: "var(--color-ide-text)" }}
        >
          What&apos;s wrong?
        </label>
        <textarea
          autoFocus
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
          }}
          placeholder="Describe what went wrong. Be as specific as you like."
          rows={5}
          className="w-full text-xs rounded border p-2 font-[inherit] resize-y"
          style={{
            borderColor: "var(--color-ide-border)",
            backgroundColor: "var(--color-ide-bg)",
            color: "var(--color-ide-text)",
          }}
        />

        <div className="flex flex-col gap-1">
          <span
            className="text-xs font-semibold"
            style={{ color: "var(--color-ide-text)" }}
          >
            Category (optional)
          </span>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {CATEGORIES.map((c) => (
              <label
                key={c.value}
                className="flex items-center gap-1 text-xs cursor-pointer"
                style={{ color: "var(--color-ide-text-muted)" }}
              >
                <input
                  type="radio"
                  name="report-category"
                  checked={category === c.value}
                  onChange={() => {
                    setCategory(c.value);
                  }}
                />
                {c.label}
              </label>
            ))}
          </div>
        </div>

        <details className="text-xs">
          <summary
            className="cursor-pointer select-none"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Context that will be attached
          </summary>
          <ul
            className="mt-1 pl-4 list-disc"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            <li>
              Session: <code>{defaults?.sessionId ?? "—"}</code>
            </li>
            <li>
              User: {defaults?.userEmail ?? "—"} (tenant{" "}
              <code>{defaults?.tenant ?? "—"}</code>)
            </li>
            <li>
              Design:{" "}
              {designSummary !== null ? (
                <code>{designSummary}</code>
              ) : (
                <em>(none open)</em>
              )}
            </li>
            <li>
              Build: <code>{defaults?.env ?? "—"}</code> @{" "}
              <code>{defaults?.commitHash ?? "—"}</code>
            </li>
          </ul>
        </details>

        {errorMessage !== null && (
          <p className="text-xs" style={{ color: "var(--color-ide-error)" }}>
            {errorMessage}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="px-3 py-1.5 text-xs rounded border"
            style={{
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text-muted)",
              opacity: submitting ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => void handleSubmit()}
            disabled={submitting || description.trim().length === 0}
            className="px-3 py-1.5 text-xs rounded font-semibold"
            style={{
              backgroundColor: "var(--color-ide-accent, #3b82f6)",
              color: "white",
              opacity: submitting || description.trim().length === 0 ? 0.5 : 1,
            }}
          >
            {submitting ? "Submitting…" : "Submit"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
